import { getClassroomApiUrl } from "@/js/utils/getBackendUrl";
import * as clsApi from "@/js/api/classroomApi";

export type RuleTargetType = "URL" | "SIGNATURE";

export type RuleType = "BLOCKED" | "ALLOWED";

export type ClassroomRule = {
	id: string;
	target: string;
	type: RuleTargetType;
	action: RuleType;
	createdAt: number;
};

export type RulesLibraryList = {
	id: string;
	name: string;
	classroomRules: ClassroomRule[];
	createdAt: number;
};

export type GetRulesLibraryResponse = {
	classroomRulesLists: RulesLibraryList[];
};

export type RulesLibrarySuggestion = {
	id: string;
	target: string;
	type: RuleTargetType;
	action: RuleType;
};

export type GetRulesLibrarySuggestionsResponse = {
	rulesLibrarySuggestions: RulesLibrarySuggestion[];
};

export type ClassroomRuleRequest = {
	id: string;
	target: string;
	type: RuleTargetType;
	action: RuleType;
};

export type UpdateClassroomRuleRequest = {
	id: string;
	action: RuleType;
};

export type CreateRulesLibraryListRequest = {
	name: string;
	classroomRules: ClassroomRuleRequest[];
};

export type UpdateRulesLibraryListRequest = {
	name: string;
	updated?: UpdateClassroomRuleRequest[];
	deleted?: string[];
	added?: ClassroomRuleRequest[];
};

export type PromoteInClassRuleToListRequest = {
	ruleId: string;
	classId: string;
};

export type ValidateRuleRequest = {
	candidate: ClassroomRuleRequest;
	draft: ClassroomRuleRequest[];
};

export type RulesLibraryValidationErrorItem = {
	code: string;
	message: string;
	relatedRuleId?: string;
};

export type RulesLibraryErrorResponse = {
	errors?: RulesLibraryValidationErrorItem[];
};

export class RulesLibraryValidationError extends Error {
	readonly errors: RulesLibraryValidationErrorItem[];
	readonly status: number;

	constructor(errors: RulesLibraryValidationErrorItem[], status: number) {
		super(errors[0]?.message ?? `Rules library validation failed: ${status}`);
		this.name = "RulesLibraryValidationError";
		this.errors = errors;
		this.status = status;
	}
}

export function isRulesLibraryValidationError(error: unknown): error is RulesLibraryValidationError {
	if (error instanceof RulesLibraryValidationError) {
		return true;
	}

	return (
		typeof error === "object" &&
		error !== null &&
		"name" in error &&
		error.name === "RulesLibraryValidationError" &&
		"errors" in error &&
		Array.isArray((error as RulesLibraryValidationError).errors)
	);
}

/**
 * Rejects with the `{ errors: [...] }` body CPS returns for 400/404/409, which classroom-api
 * forwards unchanged. Shared with the classroom-rules endpoints, which use the same shape.
 */
export async function throwRulesLibraryRequestError(response: Response, fallbackMessage: string): Promise<never> {
	let body: RulesLibraryErrorResponse = {};
	try {
		body = (await response.json()) as RulesLibraryErrorResponse;
	} catch {
		body = {};
	}

	if (Array.isArray(body.errors) && body.errors.length > 0) {
		throw new RulesLibraryValidationError(body.errors, response.status);
	}

	throw new Error(`${fallbackMessage}: ${response.status}`);
}

function rulesLibraryListUrl(host: string, applianceId: string, listId: string): string {
	return `${host}/v1/appliances/${encodeURIComponent(applianceId)}/teachers/rules-library/lists/${encodeURIComponent(listId)}`;
}

export async function getRulesLibrary(
	applianceId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetRulesLibraryResponse> {
	const response = await fetch(`${host}/v1/appliances/${encodeURIComponent(applianceId)}/teachers/rules-library`, {
		method: "GET",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch rules library: ${response.status}`);
	}

	return (await response.json()) as GetRulesLibraryResponse;
}

export async function getRulesLibrarySuggestions(
	applianceId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetRulesLibrarySuggestionsResponse> {
	const response = await fetch(`${host}/v1/appliances/${encodeURIComponent(applianceId)}/teachers/rules-library-suggestions`, {
		method: "GET",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch rules library suggestions: ${response.status}`);
	}

	return (await response.json()) as GetRulesLibrarySuggestionsResponse;
}

export async function createRulesLibraryList(
	applianceId: string,
	body: CreateRulesLibraryListRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<RulesLibraryList> {
	const response = await fetch(`${host}/v1/appliances/${encodeURIComponent(applianceId)}/teachers/rules-library`, {
		method: "POST",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to create rules library list");
	}

	return (await response.json()) as RulesLibraryList;
}

export async function updateRulesLibraryList(
	applianceId: string,
	listId: string,
	body: UpdateRulesLibraryListRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<RulesLibraryList> {
	const response = await fetch(rulesLibraryListUrl(host, applianceId, listId), {
		method: "PATCH",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to update rules library list");
	}

	return (await response.json()) as RulesLibraryList;
}

export async function promoteInClassRuleToList(
	applianceId: string,
	listId: string,
	body: PromoteInClassRuleToListRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<RulesLibraryList> {
	const response = await fetch(`${rulesLibraryListUrl(host, applianceId, listId)}/promoted-rules`, {
		method: "POST",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to promote in-class rule to list");
	}

	return (await response.json()) as RulesLibraryList;
}

export async function deleteRulesLibraryList(
	applianceId: string,
	listId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(rulesLibraryListUrl(host, applianceId, listId), {
		method: "DELETE",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to delete rules library list: ${response.status}`);
	}
}

export async function validateRulesLibraryRule(
	applianceId: string,
	body: ValidateRuleRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(`${host}/v1/appliances/${encodeURIComponent(applianceId)}/teachers/rules-library/validate`, {
		method: "POST",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to validate rules library rule");
	}
}
